import { useState } from 'react';
import { Check, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EXPERIENCE_CATEGORIES } from '@/constants/experienceCategories';

export interface AdventureTypeFilterProps {
  selected: string[];
  onChange: (selected: string[]) => void;
}

// Adventure types extracted from experience categories
export const ADVENTURE_TYPES = EXPERIENCE_CATEGORIES
  .filter(category => category.type === 'adventure' || category.type === 'both')
  .map(category => ({
    id: category.id,
    name: category.name,
    emoji: category.emoji,
    description: category.description
  }));

export function AdventureTypeFilter({ selected, onChange }: AdventureTypeFilterProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const toggleAdventureType = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter(type => type !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const filteredAdventureTypes = searchQuery.trim() === ''
    ? ADVENTURE_TYPES
    : ADVENTURE_TYPES.filter(adventure => 
        adventure.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        adventure.description.toLowerCase().includes(searchQuery.toLowerCase())
      );

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <div className="relative">
          <input
            type="text"
            placeholder="Search adventure types..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <Search className="h-4 w-4" />
          </div>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Selected Adventures Pills */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map(id => {
            const adventure = ADVENTURE_TYPES.find(a => a.id === id);
            if (!adventure) return null;
            
            return (
              <div 
                key={id}
                className="inline-flex items-center bg-primary/10 text-primary px-2 py-1 rounded-full text-xs"
              >
                <span className="mr-1">{adventure.emoji}</span>
                <span>{adventure.name}</span>
                <button 
                  className="ml-1 text-primary hover:text-[#1F4220]"
                  onClick={() => toggleAdventureType(id)}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
          {selected.length > 1 && (
            <button 
              onClick={() => onChange([])}
              className="text-xs text-gray-500 hover:text-gray-800"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Adventure Types Grid */}
      <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar-dark">
        {filteredAdventureTypes.map((adventure) => {
          const isSelected = selected.includes(adventure.id);
          return (
            <button
              key={adventure.id}
              onClick={() => toggleAdventureType(adventure.id)}
              className={cn(
                "flex items-start p-3 rounded-lg transition-colors border text-left",
                isSelected 
                  ? "bg-primary/10 border-primary/30 text-primary"
                  : "bg-white border-gray-200 hover:bg-gray-50 text-gray-700"
              )}
            >
              <div className="w-8 h-8 flex items-center justify-center mr-3 text-xl flex-shrink-0">
                {adventure.emoji}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{adventure.name}</span>
                  {isSelected && (
                    <Check className="h-4 w-4 ml-2 flex-shrink-0" />
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                  {adventure.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {filteredAdventureTypes.length === 0 && (
        <p className="text-gray-500 text-sm py-3 text-center">
          No adventure types found matching "{searchQuery}"
        </p>
      )}

      {/* Selected Count */}
      {selected.length > 0 && (
        <div className="pt-2 text-xs text-gray-500">
          {selected.length} adventure type{selected.length !== 1 ? 's' : ''} selected
        </div>
      )}
    </div>
  );
} 